import { activeRouteStore, getAttemptedTransitionUrl, setAttemptedTransitionUrl } from '@customerio/hydra-nav-state';
import { useStore } from '@nanostores/react';
import { redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { getSampleAccounts } from '@/features/workspaces';
import Journeys from '@/pages/journeys';
import { useAuthQuery } from '@/services/auth';
import { useLocation, useParams } from '@/services/router/compat';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { AppBundleRoutes } from '@/utils/app-bundles';
import { sentryCaptureLegacyRedirect } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { queryClient } from '@/services/query-client';

export function JourneysWithAuthRefresh() {
  const auth = useAuthQuery();
  const location = useLocation();
  const activeRoute = useStore(activeRouteStore);

  useEffect(() => {
    if (auth.data == null && getAttemptedTransitionUrl() == null && window.location.pathname.startsWith('/login')) {
      setAttemptedTransitionUrl(window.location.origin);
    }
    // oxlint-disable-next-line react/exhaustive-deps -- Only needs to re-run when location changes
  }, [location]);

  useEffect(() => {
    if (!isEmptyString(activeRoute) && location.pathname !== window.location.pathname && !auth.isFetching) {
      auth.refetch();
    }
    // oxlint-disable-next-line react/exhaustive-deps -- refetch is stable; listing `auth` here re-runs the effect on every query-state change (including the refetch above), causing a login loop that reboots the embedded Journeys app on /login (JOURNEYS-UI-11HZ)
  }, [activeRoute, location]);

  // Renders the single shared background instance (see the boot contract in
  // pages/journeys.tsx). The auth-refresh effects above are mount-agnostic — they detect
  // login completion via activeRoute divergence and refetch auth — and are preserved
  // verbatim (including the JOURNEYS-UI-11HZ deps fix) so behavior is unchanged apart
  // from no longer booting a second, destroy-on-unmount Ember instance.
  return <Journeys />;
}

export const preHydraJourneysEnvRegex = /^\/env\/(last|(\d+))((\/.*$)|$)/;
export const v1HydraJourneysRegex = /^\/journeys\/env\/(last|(\d+))((\/.*$)|$)/;
export const oldParcelRegex = /^\/workspaces\/(\d+)\/parcel((\/.*$)|$)/;

export function PipelinesLegacyRedirect() {
  const params = useParams({ strict: false });
  const splat = params._splat ?? '';
  const targetSegments = ['', 'workspaces', params.workspaceId ?? '', 'pipelines'];
  if (splat.length > 0) {
    targetSegments.push(splat);
  }
  const target = targetSegments.join('/');
  return <SafeNavigate to={target} options={{ shouldReplaceHistoryEntry: true }} />;
}

// Design Studio is hydra's own pages rather than an embedded app, so it is not an `AppBundle`.
const REDIRECT_TARGET_ROUTES = { ...AppBundleRoutes, 'design-studio': '/design-studio' } as const;

type RedirectTarget = keyof typeof REDIRECT_TARGET_ROUTES;

type RedirectRegexToTargetAppProps = { regex: RegExp; targetApp: RedirectTarget };

type GetRedirectRegexTargetOptions = RedirectRegexToTargetAppProps & {
  pathname: string;
  search: string;
};

export function getRedirectRegexTarget({ regex, targetApp, pathname, search }: GetRedirectRegexTargetOptions) {
  let path = pathname;
  const matches = pathname.match(regex);

  if (matches != null) {
    const [, workspaceId, _, rest] = matches;
    let adjustedRest = rest != null ? rest.replace(/^\//, '') : '';
    path = ['', 'workspaces', workspaceId].join('/');

    if (targetApp === 'design-studio' && adjustedRest.startsWith('templates')) {
      adjustedRest = 'dashboard';
    }

    path = adjustedRest.startsWith('settings')
      ? [path, adjustedRest].join('/')
      : [path, REDIRECT_TARGET_ROUTES[targetApp].replace(/^\//, ''), adjustedRest].filter(Boolean).join('/');

    path = `${path}${search}`;
  } else {
    path = ['', 'workspaces'].join('/') + search;
  }

  return path;
}

export function RedirectRegexToTargetApp({ regex, targetApp }: RedirectRegexToTargetAppProps) {
  const path = getRedirectRegexTarget({
    regex,
    targetApp,
    pathname: window.location.pathname,
    search: window.location.search,
  });

  return <SafeNavigate to={path} options={{ shouldReplaceHistoryEntry: true }} />;
}

type LegacyRedirectReporterProps = { children: ReactNode };

export function LegacyRedirectReporter({ children }: LegacyRedirectReporterProps) {
  const location = useLocation();
  const params = useParams({ strict: false });

  const genericPath = Object.entries(params).reduce((accumulator, [key, value]) => {
    if (value == null) {
      return accumulator;
    }

    return accumulator.replace(String(value), key);
  }, location.pathname);

  useEffect(() => {
    sentryCaptureLegacyRedirect(genericPath);
  }, [genericPath]);

  return children;
}

export function legacyRedirect(to: string) {
  return ({ params }: { params: Record<string, string | undefined> }) => {
    const [pathname = '', search = ''] = to.replace(/:(\w+)/g, (_, key) => params[key] ?? '').split('?');
    throw redirect({
      to: pathname,
      search: search.length > 0 ? Object.fromEntries(new URLSearchParams(search)) : true,
    });
  };
}

type GetLegacyWorkspaceSettingsRedirectTargetOptions = {
  search: string;
  splat?: string;
  workspaceId: string;
};

export function getLegacyWorkspaceSettingsRedirectTarget({
  search,
  splat = '',
  workspaceId,
}: GetLegacyWorkspaceSettingsRedirectTargetOptions) {
  const settingsPath = ['', 'workspaces', workspaceId, 'settings'].join('/');
  const normalizedSplat = splat.replace(/^\/+/, '');
  const pathname = [settingsPath, normalizedSplat].filter(Boolean).join('/');

  return `${pathname}${search}`;
}

export function LegacyWorkspaceSettingsRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const target = getLegacyWorkspaceSettingsRedirectTarget({
    search: location.search,
    splat: params._splat,
    workspaceId: params.workspaceId ?? '',
  });

  useEffect(() => {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    void navigate({ to: target as never, replace: true });
  }, [navigate, target]);

  return null;
}

export async function sampleWorkspaceRedirect() {
  const { workspaceId } = await queryClient.fetchQuery({
    queryKey: ['sampleAccounts'],
    queryFn: getSampleAccounts,
  });

  // getSampleAccounts falls back to an empty environment_ids list when there's no sample
  // workspace, so workspaceId can be undefined at runtime despite its number typing.
  if (workspaceId == null) {
    throw new Error('Missing ":workspaceId" param');
  }

  throw redirect({ to: '/workspaces/$workspaceId/journeys', params: { workspaceId } });
}

export function JourneysLegacyRoute() {
  return (
    <LegacyRedirectReporter>
      {/* Shared background instance — see the boot contract in pages/journeys.tsx. */}
      <Journeys />
    </LegacyRedirectReporter>
  );
}

export function JourneysLegacyAuthRefreshRoute() {
  return (
    <LegacyRedirectReporter>
      <JourneysWithAuthRefresh />
    </LegacyRedirectReporter>
  );
}

export function LogoutRoute() {
  return (
    <LegacyRedirectReporter>
      <Journeys />
    </LegacyRedirectReporter>
  );
}
